import { useState } from 'react'
import { type ZoneGuideData, PHASE_LABELS, WINDOW_LABELS, WEEKDAY_LABELS, rand, percent } from '../../utils/competition'

/**
 * Where and when the fleet earns most an online hour, as driver_zone_guide_v1
 * returns it. Aggregates only, so it is the same on the cockpit and the driver
 * board. Opens on the part of the month it is today, because that is the one a
 * driver can act on.
 */
export function ZoneGuide({ guide, today }: { guide: ZoneGuideData | null | undefined; today?: string }) {
  const [phase, setPhase] = useState<string>(() => phaseOf(today))
  const [window, setWindow] = useState<'am_pm' | 'pm_am'>('am_pm')
  if (!guide) return null
  const cell = guide.phases.find((p) => p.phase === phase && p.window === window)
  const days = guide.weekdays.filter((d) => d.window === window)
  const best = Math.max(0, ...days.map((d) => Number(d.rph ?? 0)))

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <h2 className="text-sm font-semibold">Where and when the fleet earns most</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Last {guide.days} days in Gauteng. {guide.basis}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {Object.keys(PHASE_LABELS).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPhase(p)}
            className={`rounded-md border px-2 py-1 text-xs ${p === phase ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] font-medium' : 'border-border text-muted-foreground hover:bg-muted'}`}
          >
            {PHASE_LABELS[p]}
          </button>
        ))}
        <span className="mx-1 w-px bg-border" aria-hidden="true" />
        {(['am_pm', 'pm_am'] as const).map((w) => (
          <button
            key={w}
            type="button"
            onClick={() => setWindow(w)}
            className={`rounded-md border px-2 py-1 text-xs ${w === window ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] font-medium' : 'border-border text-muted-foreground hover:bg-muted'}`}
          >
            {WINDOW_LABELS[w]}
          </button>
        ))}
      </div>

      {!cell ? (
        <p className="mt-3 text-sm text-muted-foreground">No shifts in this part of the month yet.</p>
      ) : (
        <>
          <p className="mt-3 text-sm">
            Fleet average: <span className="font-semibold tabular-nums">{rand(cell.fleet_rph)}</span> an online hour,{' '}
            <span className="tabular-nums">{rand(cell.fleet_eps)}</span> a shift, over {cell.shifts} shifts.
          </p>
          {cell.zones.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">No area has enough shifts yet to compare.</p>
          ) : (
            <div className="mt-2 overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Area</th>
                    <th className="px-3 py-2 text-right">An hour</th>
                    <th className="px-3 py-2 text-right">A shift</th>
                    <th className="px-3 py-2 text-right">Busy</th>
                    <th className="px-3 py-2 text-right">Shifts</th>
                  </tr>
                </thead>
                <tbody>
                  {cell.zones.map((z) => (
                    <tr key={z.zone} className="border-t border-border">
                      <td className="px-3 py-2 font-medium">{z.zone}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{rand(z.rph)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{rand(z.eps)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{percent(z.busy_share)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{z.shifts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-1 text-[11px] text-muted-foreground">Busy is the share of online time spent on a trip.</p>
        </>
      )}

      <h3 className="mt-4 text-xs font-semibold text-muted-foreground">{WINDOW_LABELS[window]} by day of the week, all of the month</h3>
      <div className="mt-2 grid grid-cols-7 gap-1 text-center text-xs">
        {days.map((d) => {
          const top = best > 0 && Number(d.rph ?? 0) >= best * 0.97
          return (
            <div key={d.dow} className={`rounded-md p-1.5 ${top ? 'bg-[hsl(var(--brand-soft))] font-semibold' : 'bg-muted/60'}`}>
              <p className="text-muted-foreground">{WEEKDAY_LABELS[d.dow]}</p>
              <p className="tabular-nums">{rand(d.rph)}</p>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function phaseOf(iso?: string): string {
  const day = iso ? Number(iso.slice(8, 10)) : new Date().getDate()
  if (day >= 25 || day <= 5) return 'days_25_to_5'
  if (day <= 14) return 'days_6_to_14'
  return 'days_15_to_24'
}
